import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { euro, useCart } from "@/cart";

const API = process.env.REACT_APP_BACKEND_URL;

function Shell({ children }) {
  return (
    <div data-testid="payment-page" className="flex min-h-screen items-center justify-center bg-[#070707] px-5 py-16 text-neutral-200">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="w-full max-w-lg border border-red-900/40 bg-[#0D0D0D] p-8 sm:p-10">{children}</div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("paid");
          clear();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        /* retry */
      }
      attempts.current += 1;
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  return (
    <Shell>
      {state === "checking" && (
        <div data-testid="payment-checking" className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-red-600" />
          <p className="font-mono-tac mt-5 text-xs uppercase tracking-[0.3em] text-neutral-400">Verifica del pagamento…</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <span className="inline-flex h-12 w-12 items-center justify-center bg-[#C8102E] text-white"><Check className="h-6 w-6" /></span>
          <p className="font-mono-tac mt-6 text-[10px] uppercase tracking-[0.35em] text-red-600">Ordine confermato</p>
          <h1 className="font-display mt-2 text-5xl uppercase leading-none text-neutral-100">Grazie. Sei dei nostri.</h1>
          <p className="mt-4 text-sm leading-relaxed text-neutral-400">Pagamento ricevuto. Ti arriva la ricevuta via email da Stripe; noi prepariamo il pacco e ti scriviamo quando parte.</p>
          {order && (
            <ul data-testid="payment-order-items" className="mt-6 space-y-2 border-t border-neutral-800 pt-4 text-sm">
              {order.items.map((i, k) => (
                <li key={k} className="flex justify-between text-neutral-300">
                  <span>{i.name}{i.size ? ` · ${i.size}` : ""} × {i.quantity}</span>
                  <span className="font-mono-tac">{euro(i.unit_cents * i.quantity)}</span>
                </li>
              ))}
              <li className="flex justify-between border-t border-neutral-800 pt-2 font-bold text-neutral-100"><span>Totale (spedizione inclusa)</span><span className="font-mono-tac">{euro(order.total_cents)}</span></li>
              <li className="font-mono-tac pt-1 text-[10px] uppercase tracking-[0.2em] text-neutral-600">Ordine #{order.id.slice(0, 8)}</li>
            </ul>
          )}
        </div>
      )}
      {(state === "failed" || state === "error") && (
        <div data-testid="payment-failed">
          <span className="inline-flex h-12 w-12 items-center justify-center border border-red-900 text-red-500"><X className="h-6 w-6" /></span>
          <h1 className="font-display mt-6 text-4xl uppercase text-neutral-100">Pagamento non completato</h1>
          <p className="mt-3 text-sm text-neutral-400">Nessun addebito effettuato. Puoi riprovare dal carrello.</p>
        </div>
      )}
      {state === "timeout" && (
        <div data-testid="payment-timeout">
          <h1 className="font-display text-4xl uppercase text-neutral-100">Stiamo ancora verificando</h1>
          <p className="mt-3 text-sm text-neutral-400">La conferma di Stripe sta tardando. Se hai ricevuto la ricevuta via email, l'ordine è andato a buon fine: lo vedremo noi nel pannello.</p>
        </div>
      )}
      <Link data-testid="payment-back-home" to="/" className="mt-8 inline-flex items-center gap-2 border border-neutral-700 px-5 py-3 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-300 hover:border-red-600 hover:text-red-500">
        ← Torna al sito
      </Link>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div data-testid="payment-cancel">
        <p className="font-mono-tac text-[10px] uppercase tracking-[0.35em] text-red-600">Pagamento annullato</p>
        <h1 className="font-display mt-2 text-5xl uppercase leading-none text-neutral-100">Nessun problema.</h1>
        <p className="mt-4 text-sm leading-relaxed text-neutral-400">Non è stato addebitato nulla. Il carrello è ancora lì quando vuoi.</p>
        <Link data-testid="payment-back-home" to="/#merch" className="mt-8 inline-flex items-center gap-2 border border-neutral-700 px-5 py-3 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-300 hover:border-red-600 hover:text-red-500">
          ← Torna al merch
        </Link>
      </div>
    </Shell>
  );
}
